// src/stores/useActivityStore.js
import { defineStore } from "pinia";
import * as activityService from "@/services/activityService";
import * as noteService from "@/services/noteService"; // Mantido caso getNotesByActivityId ainda seja usado
import { useTextStore } from "./useTextStore";
import { useUserStore } from "./useUserStore";
import { useNoteStore } from "./useNoteStore";
import { useManagedAccountStore } from "./useManagedAccountStore";
import {
  readLastSeries,
  writeLastSeries,
  forgetLastSeries,
} from "@/utils/lastSeries";

export const useActivityStore = defineStore("activity", {
  state: () => ({
    activities: [],
    selectedActivityIds: [],

    // ✅ ID da atividade atualmente selecionada (para editar, visualizar, ir para RightArea, etc.)
    idActivity: null,

    // ✅ ID do TEXTO usado como filtro nas buscas paginadas de atividades
    currentTextId: null,

    error: null,
    totalCount: 0,
    totalPages: 1,
    currentPage: 1,
    pageSize: 10,
    searchTerm: "",

    // ======================================================
    // PATCH: flag "Minhas atividades"
    // ======================================================
    myActivitiesOnly: false,

    shouldForcePageOne: false,
    selectedActivity: null,

    // --- Estado para permissões da atividade selecionada ---
    activityPermissions: [],
  }),

  getters: {
    // Mantido: checa se TODOS os itens da listagem (total) estão selecionados
    isAllSelected: (state) => (total) => {
      return state.selectedActivityIds.length === total && total > 0;
    },

    // Verifica se TODOS os itens da PÁGINA ATUAL estão selecionados
    isAllOnPageSelected: (state) => (currentPageItems) => {
      if (!currentPageItems || currentPageItems.length === 0) return false;
      return currentPageItems.every((item) =>
        state.selectedActivityIds.includes(item.activityId),
      );
    },
  },

  actions: {
    // ======================================================
    // PATCH: helper para ativar/desativar o filtro
    // ======================================================
    setMyActivitiesOnly(value) {
      this.myActivitiesOnly = value;
    },

    // Define o ID da atividade atualmente selecionada
    setIdActivity(id) {
      this.idActivity = id;
    },

    // Define o ID do texto usado como filtro para as atividades
    setCurrentTextId(textId) {
      this.currentTextId = textId ?? null;
    },

    // Gerenciamento de seleção de múltiplos itens na tabela
    toggleSelection(id) {
      const index = this.selectedActivityIds.indexOf(id);
      if (index > -1) {
        this.selectedActivityIds.splice(index, 1);
      } else {
        this.selectedActivityIds.push(id);
      }
    },

    // Adiciona os IDs da página atual à seleção existente.
    selectAllOnPage(ids) {
      const uniqueIds = new Set([...this.selectedActivityIds, ...ids]);
      this.selectedActivityIds = Array.from(uniqueIds);
    },

    // Remove os IDs da página atual da seleção existente.
    deselectAllOnPage(idsToRemove) {
      const idsToRemoveSet = new Set(idsToRemove);
      this.selectedActivityIds = this.selectedActivityIds.filter(
        (id) => !idsToRemoveSet.has(id),
      );
    },

    selectAll(ids) {
      this.selectedActivityIds = [...ids];
    },

    // Limpa TODA a seleção
    clearSelection() {
      this.selectedActivityIds = [];
    },

    // --- AÇÕES DE BUSCA ---

    // Busca TODAS as atividades (sem paginação/filtros específicos)
    async fetchActivities() {
      try {
        const response = await activityService.fetchActivities();
        this.activities = response.data;
      } catch (error) {
        this.error = error.response?.data?.message || error.message;
        console.error("❌ Erro ao buscar atividades:", this.error);
        throw error;
      }
    },

    // Busca UMA ÚNICA atividade por ID e suas notas associadas
    async getActivityById(id) {
      try {
        const response = await activityService.getActivityById(id);
        const activityData = response.data;

        this.selectedActivity = activityData;

        // ✅ Busca TODAS as notas associadas a esta atividade
        const allNotesResponse = await noteService.getNotesByActivityId(id);
        this.selectedActivity.allNotes = allNotesResponse.data;

        // ✅ ENRIQUECIMENTO: busca o texto relacionado para obter o criador do texto
        if (this.selectedActivity?.textId) {
          try {
            const textStore = useTextStore();
            const text = await textStore.getTextById(
              this.selectedActivity.textId,
            );

            this.selectedActivity.textCreatedByUserId =
              text?.createdByUserId ?? null;

            this.selectedActivity.textCreatedByUserName =
              text?.createdByUserName ?? null;
          } catch (textError) {
            console.warn(
              "⚠️ Não foi possível enriquecer a atividade com os dados do criador do texto.",
              textError,
            );

            this.selectedActivity.textCreatedByUserId = null;
            this.selectedActivity.textCreatedByUserName = null;
          }
        } else {
          this.selectedActivity.textCreatedByUserId = null;
          this.selectedActivity.textCreatedByUserName = null;
        }

        return this.selectedActivity;
      } catch (error) {
        const mensagem =
          error.response?.data?.message ||
          error.response?.data ||
          error.message;
        console.error("❌ Erro ao obter atividade:", mensagem);
        throw new Error(mensagem);
      }
    },

    // Busca atividades paginadas com filtro opcional por termo de busca e TextId
    // Busca atividades paginadas com filtro opcional por termo de busca e TextId
    async fetchActivitiesPaged(
      pageNumber,
      pageSize,
      search = null,
      textId = null,
    ) {
      try {
        // ======================================================
        // PATCH: mantém store sincronizada
        // ======================================================
        this.currentPage = pageNumber;
        this.pageSize = pageSize;

        if (typeof search === "string") {
          this.searchTerm = search;
        }

        this.currentTextId = textId ?? null;

        const response = await activityService.fetchActivitiesPaged(
          pageNumber,
          pageSize,
          this.searchTerm,
          this.currentTextId,
          this.myActivitiesOnly, // ✅ PATCH
        );

        this.activities = response.data.items;
        this.totalCount = response.data.totalCount;
        this.totalPages = response.data.totalPages;
        this.currentPage = pageNumber;
      } catch (error) {
        const mensagem =
          error.response?.data?.message ||
          error.response?.data ||
          error.message;
        this.error = mensagem;
        console.error("❌ Erro ao buscar atividades paginadas:", mensagem);
        throw new Error(mensagem);
      }
    },

    // ======================================================
    // Fluxo "escolher texto → anotar" (sem a tela de atividades)
    // ======================================================

    // Prepara as stores para abrir a tela de anotação de um texto, resolvendo
    // qual série usar: a preferida / a última usada (localStorage) e, em último
    // caso, o get-or-create da série padrão no backend. NÃO navega — o chamador
    // faz router.push({ name: "RightArea" }) com a atividade retornada.
    async openTextForAnnotation(textId, { preferActivityId = null } = {}) {
      const textStore = useTextStore();
      const noteStore = useNoteStore();
      const userId = useUserStore().currentUser?.userId ?? null;

      // Params de rota chegam como string ("123"): normaliza para comparar por
      // valor, senão o candidato é descartado e cai no resolve (série padrão).
      const tId = Number(textId);
      const preferred = preferActivityId != null ? Number(preferActivityId) : null;

      await textStore.getTextById(tId);
      textStore.setIdText(tId);

      const candidateId = preferred ?? readLastSeries(userId, tId) ?? null;

      // O círculo alimenta as travas de nota da tela de anotação (o membro abre
      // qualquer série que enxerga, mas só anota nas do círculo). Carregar aqui
      // evita que a tela apareça por um instante com os botões no estado errado.
      const managedAccountStore = useManagedAccountStore();
      if (managedAccountStore.currentMemberPermissions?.restrictToTitular) {
        await managedAccountStore.ensureCircle();
      }

      let activity = null;

      if (candidateId) {
        try {
          const found = await this.getActivityById(candidateId);
          if (
            found &&
            Number(found.textId) === tId &&
            found.active !== false
          ) {
            activity = found;
          }
        } catch {
          // série lembrada foi apagada ou perdeu acesso — segue para o resolve
        }
        // Só esquece a última série se o candidato veio do localStorage; um
        // preferActivityId inválido (ex.: série de outro texto) não deve apagar
        // a memória do texto.
        if (!activity && preferred == null) forgetLastSeries(userId, tId);
      }

      if (!activity) {
        const resolved = (
          await activityService.resolveActivityForText(tId)
        ).data;
        activity = await this.getActivityById(resolved.activityId);
      }

      this.setIdActivity(activity.activityId);
      this.setCurrentTextId(tId);
      writeLastSeries(userId, tId, activity.activityId);
      activityService.touchActivity(activity.activityId).catch(() => {});

      await noteStore.resetNoteFiltersToDefault();

      return activity;
    },

    // Troca a série ativa SEM sair da tela de anotação. O :key em RightArea
    // remonta TextDisplayer/NoteSection ao mudar idActivity — por isso carrega a
    // série (selectedActivity) ANTES de trocar o id, para o remount já nascer
    // coerente. resetNoteFiltersToDefault limpa filtros e faz o primeiro fetch.
    async switchSeries(activityId) {
      const textStore = useTextStore();
      const noteStore = useNoteStore();
      const userId = useUserStore().currentUser?.userId ?? null;

      const activity = await this.getActivityById(activityId);
      this.setIdActivity(activityId);

      const textId = activity?.textId ?? textStore.idText;
      if (textId) {
        this.setCurrentTextId(textId);
        writeLastSeries(userId, textId, activityId);
      }
      activityService.touchActivity(activityId).catch(() => {});

      await noteStore.resetNoteFiltersToDefault();

      return activity;
    },

    // Cria uma nova série para o texto em contexto e já troca para ela. Usado
    // pelo "➕ Nova série" do seletor no cabeçalho da anotação. Sem permissões no
    // payload: o backend aplica o padrão "Público".
    async createSeriesForCurrentText(title) {
      const textStore = useTextStore();
      const textId = textStore.idText;
      if (!textId) throw new Error("Nenhum texto em contexto.");

      const clean = (title || "").trim();
      if (clean.length < 3) {
        throw new Error("O nome da série deve ter ao menos 3 caracteres.");
      }

      const response = await this.createActivity({
        title: clean,
        textId,
        status: "Em andamento",
        active: true,
        isDefault: false,
        permissions: [],
        startedAt: new Date().toISOString(),
      });

      const newId = response?.data?.activityId ?? null;
      if (newId) await this.switchSeries(newId);
      return newId;
    },

    // --- CRIAÇÃO / EDIÇÃO / EXCLUSÃO ---

    async createActivity(data) {
      try {
        const createdActivity = await activityService.createActivity(data);

        // Atualiza o usuário para o store detectar a transição first_activity_created
        // (false→true) e disparar o evento de 1ª ativação. Também alinha cotas.
        try {
          await useUserStore().refreshCurrentUser();
        } catch (e) {
          console.warn("[useActivityStore] Falha ao atualizar usuário após criar atividade:", e?.message || e);
        }

        // Garante que o filtro de texto atual esteja alinhado com o texto da nova atividade
        if (data?.textId) {
          this.setCurrentTextId(data.textId);
        }

        this.shouldForcePageOne = true;

        // Recarrega a primeira página com o termo de busca atual e o TextId em uso
        await this.fetchActivitiesPaged(
          1,
          this.pageSize,
          this.searchTerm,
          this.currentTextId,
        );

        return createdActivity;
      } catch (error) {
        console.error(
          "❌ Erro ao cadastrar atividade na store:",
          error.response?.data || error.message || error,
        );
        throw error;
      }
    },

    async updateActivity(data) {
      // O 'data' recebido do formulário já deve conter o campo 'active'
      try {
        const response = await activityService.updateActivity(data);
        const updatedActivity = response.data.activity; // A API retorna a atividade atualizada

        // Atualiza o item na lista local para refletir a mudança instantaneamente
        const index = this.activities.findIndex(
          (a) => a.activityId === updatedActivity.activityId,
        );
        if (index !== -1) {
          this.activities[index] = updatedActivity;
        }

        // Atualiza também a atividade selecionada, se for o caso
        if (
          this.selectedActivity &&
          this.selectedActivity.activityId === updatedActivity.activityId
        ) {
          this.selectedActivity = updatedActivity;
        }
      } catch (error) {
        console.error(
          "❌ Erro ao atualizar atividade na store:",
          error.response?.data || error.message || error,
        );
        throw error;
      }
    },

    async deleteActivityById(id) {
      try {
        await activityService.deleteActivity(id);
        // Se quiser, aqui você pode recarregar a página usando this.currentTextId
        // mas seu fluxo atual já faz isso externamente via @refresh em DeleteActivityForm.
      } catch (error) {
        const serverMessage = error.response?.data?.message || error.message;
        console.error("❌ Erro ao excluir atividade:", serverMessage);

        if (
          serverMessage.includes(
            "colocar aqui mensagem de erro enviada pela api",
          )
        ) {
          throw new Error("Não é possível excluir a atividade, pois...");
        }

        this.error = serverMessage;
        throw new Error(serverMessage);
      }
    },

    async deleteSelectedActivities() {
      if (!this.selectedActivityIds.length) return;

      try {
        await activityService.deleteActivitiesBatch(this.selectedActivityIds);

        this.clearSelection();

        // Após exclusão em lote, recarrega a página atual
        // ✅ Agora usa SEMPRE o currentTextId como filtro de texto
        await this.fetchActivitiesPaged(
          this.currentPage,
          this.pageSize,
          this.searchTerm,
          this.currentTextId,
        );

        // Ajusta a página se a lista ficar vazia após a exclusão em lote
        if (this.activities.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1;
          await this.fetchActivitiesPaged(
            this.currentPage,
            this.pageSize,
            this.searchTerm,
            this.currentTextId,
          );
        }
      } catch (error) {
        const serverMessage = error.response?.data?.message || error.message;
        console.error("❌ Erro ao excluir atividades em lote:", serverMessage);

        if (
          serverMessage.includes(
            "colocar aqui mensagem de erro enviada pela api'",
          )
        ) {
          throw new Error(
            "Não é possível excluir uma ou mais atividades selecionadas, pois...",
          );
        }

        this.error = serverMessage;
        throw new Error(serverMessage);
      }
    },

    // --- FILTRO / BUSCA ---

    setSearchTerm(term) {
      this.searchTerm = term;
      this.currentPage = 1;
      this.shouldForcePageOne = true;

      // ======================================================
      // PATCH: preserva o filtro "Minhas atividades" porque
      // fetchActivitiesPaged agora usa this.myActivitiesOnly
      // ======================================================
      this.fetchActivitiesPaged(1, this.pageSize, term, this.currentTextId);
    },

    // --- PERMISSÕES DE ATIVIDADE ---

    async fetchActivityPermissions(activityId) {
      try {
        const response = await activityService.fetchActivityPermissions(
          activityId,
        );
        this.activityPermissions = response.data;
        return response.data;
      } catch (error) {
        console.error(
          "❌ Erro ao buscar permissões da atividade:",
          error.message,
        );
        this.activityPermissions = [];
        throw error;
      }
    },

    async addActivityPermission(activityId, permissionData) {
      try {
        await activityService.addActivityPermission(activityId, permissionData);
        // Após adicionar, recarregar a lista de permissões para exibir a nova
        await this.fetchActivityPermissions(activityId);
      } catch (error) {
        console.error(
          "❌ Erro ao adicionar permissão na atividade:",
          error.message,
        );
        throw error;
      }
    },

    async removeActivityPermission(activityId, userId, permissionId) {
      try {
        await activityService.removeActivityPermission(
          activityId,
          userId,
          permissionId,
        );
        // Após remover, recarregar a lista de permissões
        await this.fetchActivityPermissions(activityId);
      } catch (error) {
        console.error(
          "❌ Erro ao remover permissão da atividade:",
          error.message,
        );
        throw error;
      }
    },

    // Atualiza apenas o status Active da atividade
    async updateActivityStatus({ activityId, active }) {
      try {
        await activityService.updateActivityStatus(activityId, active);

        // Atualiza o estado local para a UI refletir a mudança sem recarregar a página
        const index = this.activities.findIndex(
          (a) => a.activityId === activityId,
        );
        if (index !== -1) {
          this.activities[index].active = active;
        }

        // Se a atividade selecionada for a que mudou, atualiza ela também
        if (
          this.selectedActivity &&
          this.selectedActivity.activityId === activityId
        ) {
          this.selectedActivity.active = active;
        }
      } catch (error) {
        console.error(
          "❌ Erro ao atualizar o status da atividade:",
          error.response?.data || error.message,
        );
        throw error;
      }
    },
  },
});
